/* eslint-disable no-restricted-globals -- DOM measurements and ResizeObserver */
import type {RefObject} from 'react';
import {useCallback, useLayoutEffect, useRef, useState} from 'react';

import {screenMinWidth} from '../../../../../provider/system/system-context-const';
import {ScreenWidthNameEnum} from '../../../../../provider/system/system-context-type';
import {useSystem} from '../../../../../provider/system/system-hook';
import {noop} from '../../../../../util/function';

const VISIBILITY_MARGIN_PX = 2;

type ReviewsFilterFooterDockReferencesType = {
    footerRef: RefObject<HTMLDivElement>;
    scrollContentRef: RefObject<HTMLDivElement>;
    scrollRootRef: RefObject<HTMLDivElement>;
};

type ReviewsFilterFooterDockLayoutType = {
    fixedFooterBox: {height: number; left: number; width: number};
    isFilterFooterFixed: boolean;
};

const EMPTY_REVIEWS_FILTER_FOOTER_DOCK_LAYOUT: ReviewsFilterFooterDockLayoutType = {
    fixedFooterBox: {height: 0, left: 0, width: 0},
    isFilterFooterFixed: false,
};

function collectFooterDockResizeTargets(
    root: HTMLDivElement,
    references: ReviewsFilterFooterDockReferencesType
): Array<Element> {
    const targets: Array<Element> = [root];
    const scrollContent = references.scrollContentRef.current;

    if (scrollContent) {
        targets.push(scrollContent);
    }

    const formElement = root.closest('form');

    if (formElement) {
        targets.push(formElement);
    }

    const footerElement = references.footerRef.current;

    if (footerElement) {
        targets.push(footerElement);
    }

    return targets;
}

function subscribeReviewsFilterFooterLayoutSync(
    references: ReviewsFilterFooterDockReferencesType & {onSync: () => void}
): () => void {
    const {footerRef, onSync, scrollContentRef, scrollRootRef} = references;
    const root = scrollRootRef.current;

    if (!root) {
        return noop;
    }

    function schedule(): void {
        requestAnimationFrame(onSync);
    }

    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;

    collectFooterDockResizeTargets(root, {footerRef, scrollContentRef, scrollRootRef}).forEach((node) => {
        resizeObserver?.observe(node);
    });

    window.addEventListener('resize', schedule, {passive: true});
    window.addEventListener('scroll', schedule, {
        capture: true,
        passive: true,
    });

    const visualViewport = window.visualViewport;

    visualViewport?.addEventListener('resize', schedule, {passive: true});
    visualViewport?.addEventListener('scroll', schedule, {passive: true});

    return function unsubscribeReviewsFilterFooterLayoutSync(): void {
        resizeObserver?.disconnect();
        window.removeEventListener('resize', schedule);
        window.removeEventListener('scroll', schedule, true);
        visualViewport?.removeEventListener('resize', schedule);
        visualViewport?.removeEventListener('scroll', schedule);
    };
}

function getReviewsFilterFooterDockLayout(input: {
    footerElement: HTMLDivElement;
    formElement: Element;
    isFilterEmpty: boolean;
    screenWidth: number;
}): ReviewsFilterFooterDockLayoutType {
    const {footerElement, formElement, isFilterEmpty, screenWidth} = input;

    const narrowLayout = screenWidth <= screenMinWidth[ScreenWidthNameEnum.tablet];
    const rect = formElement.getBoundingClientRect();
    const visualViewport = window.visualViewport;
    const viewportTop = visualViewport?.offsetTop ?? 0;
    const viewportBottom = viewportTop + (visualViewport?.height ?? window.innerHeight);
    const marginPx = VISIBILITY_MARGIN_PX;
    const isFilterFullyVisible =
        narrowLayout ||
        (rect.top >= viewportTop - marginPx &&
            rect.bottom <= viewportBottom + marginPx &&
            rect.left >= -marginPx &&
            rect.right <= window.innerWidth + marginPx);

    // Fixed dock only when the user changed filters and the card is clipped by the viewport.
    const isFilterFooterFixed = !isFilterEmpty && !isFilterFullyVisible;

    return {
        fixedFooterBox: {
            height: footerElement.offsetHeight,
            left: Math.round(rect.left),
            width: Math.round(rect.width),
        },
        isFilterFooterFixed,
    };
}

export function useReviewsFilterFooterDock(
    isFilterEmpty: boolean,
    isReviewsFilterFooterVisible: boolean
): {
    fixedFooterBox: {height: number; left: number; width: number};
    footerRef: RefObject<HTMLDivElement>;
    isFilterFooterFixed: boolean;
    scrollContentRef: RefObject<HTMLDivElement>;
    scrollRootRef: RefObject<HTMLDivElement>;
} {
    const {screen} = useSystem();
    const scrollRootRef = useRef<HTMLDivElement>(null);
    const scrollContentRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const [footerDockLayout, setFooterDockLayout] = useState<ReviewsFilterFooterDockLayoutType>(
        EMPTY_REVIEWS_FILTER_FOOTER_DOCK_LAYOUT
    );

    const syncFilterFooterLayoutRef = useRef<() => void>(noop);

    syncFilterFooterLayoutRef.current = () => {
        const root = scrollRootRef.current;
        const formElement = root?.closest('form');
        const footerElement = footerRef.current;

        if (!formElement || !footerElement || !isReviewsFilterFooterVisible) {
            setFooterDockLayout(EMPTY_REVIEWS_FILTER_FOOTER_DOCK_LAYOUT);
            return;
        }

        setFooterDockLayout(
            getReviewsFilterFooterDockLayout({
                footerElement,
                formElement,
                isFilterEmpty,
                screenWidth: screen.width,
            })
        );
    };

    const syncFilterFooterLayout = useCallback(() => {
        syncFilterFooterLayoutRef.current();
    }, []);

    useLayoutEffect(() => {
        syncFilterFooterLayout();

        return subscribeReviewsFilterFooterLayoutSync({
            footerRef,
            onSync: syncFilterFooterLayout,
            scrollContentRef,
            scrollRootRef,
        });
    }, [syncFilterFooterLayout]);

    useLayoutEffect(() => {
        syncFilterFooterLayout();
    }, [isFilterEmpty, isReviewsFilterFooterVisible, screen.width, syncFilterFooterLayout]);

    return {
        fixedFooterBox: footerDockLayout.fixedFooterBox,
        footerRef,
        isFilterFooterFixed: footerDockLayout.isFilterFooterFixed,
        scrollContentRef,
        scrollRootRef,
    };
}
